import React from 'react';
import { useTranslation } from 'react-i18next';
import { AnnotationHistoryItem } from '@/core/services/interfaces/AnnotationHistoryInterfaces';
import { TFunction } from 'i18next';

export interface AnnotationHistoryTableProps {
  data: AnnotationHistoryItem[];
}

const getStatusConfig = (t: TFunction): Record<string, {label: string, className: string}> => ({
  success: {
    label: t('sortBy.submenu.annotationHistory.status.success'),
    className: 'history-table__pill history-table__pill--success'
  },
  error: {
    label: t('sortBy.submenu.annotationHistory.status.error'),
    className: 'history-table__pill history-table__pill--error'
  },
  processing: {
    label: t('sortBy.submenu.annotationHistory.status.processing'),
    className: 'history-table__pill history-table__pill--processing'
  },
});

export const AnnotationHistoryTable: React.FC<AnnotationHistoryTableProps> = ({ data }) => {
  const { t } = useTranslation();
  const statusConfig = getStatusConfig(t);

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const maxChars = 20;

  return (
    <table className="history-table">
      <thead>
        <tr>
          <th className="history-table__col--time">{t('sortBy.submenu.annotationHistory.tableHeaders.time')}</th>
          <th className="history-table__col--count">{t('sortBy.submenu.annotationHistory.tableHeaders.count')}</th>
          <th className="history-table__col--type">{t('sortBy.submenu.annotationHistory.tableHeaders.type')}</th>
          <th className="history-table__col--fields">{t('sortBy.submenu.annotationHistory.tableHeaders.fields')}</th>
          <th className="history-table__col--status">{t('sortBy.submenu.annotationHistory.tableHeaders.status')}</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row) => {
          const hasAdd = row.fieldAdd && row.fieldAdd.length > 0;
          const hasRemove = row.fieldRemove && row.fieldRemove.length > 0

          const rawStatus = row.status ? row.status.toLocaleLowerCase() : '';
          const currentStatus = statusConfig[rawStatus] || statusConfig.processing

          const allTags = [
            ...(row.fieldAdd || []).map((tag) => ({ text: tag, isRemoved: false })),
            ...(row.fieldRemove || []).map((tag) => ({ text: tag, isRemoved: true })),
          ];

          let currentChars = 0;
          let visibleCount = 0;

          for (let i = 0; i < allTags.length; i++) {
            if (currentChars + allTags[i].text.length > maxChars && i > 0) {
              break;
            }
            currentChars += allTags[i].text.length;
            visibleCount++;
            if (currentChars > maxChars) break;
          }

          const visibleTags = allTags.slice(0, visibleCount);
          const hiddenCount = allTags.length - visibleCount;

          return (
            <tr key={row.id}>
              <td className="history-table__col--time">{formatTime(row.createdAt)}</td>
              <td className="history-table__col--count">{row.updatedCount} {t('sortBy.submenu.annotationHistory.countFormat')}</td>
              <td className="history-table__col--type">
                <div className="history-table__pills">
                  {hasAdd && <span className="history-table__pill history-table__pill--add">{t('sortBy.submenu.annotationHistory.annotationType.add')}</span>}
                  {hasRemove && <span className="history-table__pill history-table__pill--removed">{t('sortBy.submenu.annotationHistory.annotationType.remove')}</span>}
                </div>
              </td>
              <td className="history-table__col--fields">
                <div className="history-table__fields">
                  <div className="history-table__tags-group">
                    {visibleTags.map((tagObj) => (
                      <span
                        key={`${row.id}-${tagObj.text}-${tagObj.isRemoved}`}
                        className={`history-table__tag ${tagObj.isRemoved ? 'history-table__tag--removed' : ''}`}
                        title={tagObj.text}
                      >
                        {tagObj.text}
                      </span>
                    ))}
                    {hiddenCount > 0 && (
                      <span className="history-table__tag history-table__tag--more">
                        +{hiddenCount}
                      </span>
                    )}
                  </div>
                </div>
              </td>
              <td>
                <span className={currentStatus.className}>
                  {currentStatus.label}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};